import { X } from 'lucide-react'
import * as React from 'react'

import { Popover, PopoverAnchor, PopoverContent } from '#components/ui/popover'
import { cn } from '#lib/utils'

interface InlineOtpPopoverProps {
  isOpen: boolean
  onClose: () => void
  anchorRef: React.RefObject<HTMLDivElement | null>

  otpLength?: number
  disabled?: boolean
  error?: boolean
  phoneLabel?: string

  onSubmitOtp: (otp: string) => void
  onResendOtp: () => Promise<void> | void
}

export const InlineOtpPopover = ({
  isOpen,
  onClose,
  anchorRef,
  otpLength = 6,
  disabled = false,
  error = false,
  phoneLabel,
  onSubmitOtp,
  onResendOtp,
}: InlineOtpPopoverProps) => {
  const [otp, setOtp] = React.useState<string[]>(() => Array.from({ length: otpLength }, () => ''))

  const RESEND_DELAY = 30
  const [secondsLeft, setSecondsLeft] = React.useState<number>(RESEND_DELAY)
  const timerRef = React.useRef<NodeJS.Timeout | null>(null)

  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([])

  React.useEffect(() => {
    if (!isOpen) {
      setOtp(Array.from({ length: otpLength }, () => ''))
    }
  }, [isOpen, otpLength])

  React.useEffect(() => {
    if (!isOpen) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    startResendTimer()

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isOpen])

  const startResendTimer = () => {
    setSecondsLeft(RESEND_DELAY)

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return

    const next = [...otp]
    next[index] = value
    setOtp(next)

    // focus suivant
    if (value && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    // trigger final
    if (next.every(Boolean)) {
      onSubmitOtp(next.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  return (
    <Popover open={isOpen} modal>
      <PopoverAnchor asChild>
        <div ref={anchorRef} />
      </PopoverAnchor>

      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={12}
        className={cn(
          'relative z-40 rounded-xl border border-gray-200 bg-white px-8 py-4 shadow-xl',
          'animate-in fade-in slide-in-from-top-2 duration-200',
          'w-[var(--radix-popover-trigger-width)]'
        )}
      >
        {/* Arrow */}
        <div className="absolute -top-2 right-8 h-4 w-4 rotate-45 border-l border-t border-gray-200 bg-white" />

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 p-1 text-slate-400 hover:text-slate-600"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="mb-4">
          <h3 className="text-sm text-slate-900">Code de vérification</h3>

          {phoneLabel && (
            <p className="mt-1 text-xs text-slate-500">Entrez le code envoyé au {phoneLabel}</p>
          )}
        </div>

        {/* OTP INPUT */}
        <div className="flex justify-between gap-1.5">
          {otp.map((value, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              disabled={disabled}
              value={value}
              onChange={(e) => {
                handleChange(index, e.target.value)
              }}
              onKeyDown={(e) => {
                handleKeyDown(index, e)
              }}
              className={cn(
                'h-10 w-9 rounded-lg text-center text-lg font-semibold outline-none transition-all sm:h-11 sm:w-10',
                'border focus:ring-2 focus:ring-offset-1',
                error
                  ? 'border-red-300 bg-red-50 text-red-600 focus:ring-red-200'
                  : 'border-gray-200 bg-white text-gray-900 focus:border-indigo-500 focus:ring-indigo-100',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-slate-400">En attente de saisie</span>

          <button
            type="button"
            disabled={disabled || secondsLeft > 0}
            onClick={async () => {
              try {
                await onResendOtp()
                startResendTimer()
              } catch {
                //
              }
            }}
            className={cn(
              'font-medium transition-colors',
              secondsLeft > 0
                ? 'cursor-not-allowed text-slate-400'
                : 'text-indigo-600 hover:text-indigo-700',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            Renvoyer le code
            {secondsLeft > 0 && ` (${secondsLeft}s)`}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
